import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import isEqual from 'lodash/isEqual';
import { formatTimestampDate } from '../../../utils/dates';
import CommonModal from '../../Common/Modal/Modal';
import TextInput from '../../FormInputs/TextInput/TextInput';
import DateSelectorV2 from '../../FormInputs/DateSelectorV2/DateSelectorV2';
import Button from '../../Button/Button';
import { useCloseDialogWithUnsavedChanges, useUnsavedChangesWarning } from '../../../hooks';

const RouteAddEditModal = ({
  onCloseModal, title, data, onSubmit, isSaving,
}) => {
  const initialState = {
    name: data?.name || '',
    date: data?.date || '',
    description: data?.description || '',
  };
  const [formData, setFormData] = useState(initialState);
  const [isUnsavedChanges, setIsUnsavedChanges] = useState(false);
  const hasUnsavedChanges = !isEqual(initialState, formData);

  useEffect(() => {
    setIsUnsavedChanges(hasUnsavedChanges);
  }, [hasUnsavedChanges, formData]); // formData - track changes to formData for unsaved changes if isUnsavedChanges was set to false on previous changes

  const handleCloseDialog = useCloseDialogWithUnsavedChanges({
    isUnsavedChanges: hasUnsavedChanges,
    setIsUnsavedChanges,
    redirectCallback: onCloseModal,
  });

  useUnsavedChangesWarning(isUnsavedChanges);

  const handleSubmit = () => {
    // handle case where date is empty
    const validData = {
      ...formData,
      date: formData.date === '' ? formatTimestampDate(new Date()) : formData.date,
    };
    onSubmit(validData);
  };

  const buttonLabel = ((title === 'Create Route') ? 'Create' : 'Save');

  const onClick = () => {
    handleCloseDialog();
  };
  const actionRouteAddButtons = [
    {
      label: 'Cancel',
      variant: 'secondary',
      onClick,
    },
    {
      label: buttonLabel,
      variant: 'primary',
      type: 'submit',
      isDisabled: isSaving,
    },
  ];
  return (
    <CommonModal
      boxClassName="w-5/12 md:w-4/12"
      title={title}
      handleClose={onClick}
      shadow="shadow-vamos-drop-shadow"
      textAlignment="text-left"
      titlePosition="left"
      overflowY="overflow-visible"
      headerClassName="p-5.5"
      closeIconPosition="" // removed absolute position of close icon
      maxHeight="" // removed max height for whole modal
    >

      <form onSubmit={e => {
        e.preventDefault();
        handleSubmit();
      }}
      >
        <div className="px-5.5 py-8">
          <div className="grid">
            <TextInput
              label="Route Name"
              type="text"
              labelTextSize="text-sm"
              isRequired
              asteriskWidth="w-2"
              value={formData.name}
              className="pl-2 mb-5.5"
              placeholder="enter a name"
              name="text"
              setValue={(value) => {
                setFormData((prev) => ({ ...prev, name: value }));
              }}
            />
            <DateSelectorV2
              label="Date"
              value={formData.date}
              dateFormat="MM/dd/yyyy"
              inputStyle={{ padding: '0 0 0 8px' }}
              placeholder="choose date"
              name="date"
              wrapperClassName="mb-5.5"
              setValue={(value) => {
                setFormData((prev) => ({ ...prev, date: formatTimestampDate(value) }));
              }}
            />
            <TextInput
              label="Description"
              type="text"
              labelTextSize="text-sm"
              value={formData.description}
              className="pl-2"
              placeholder="describe what this route is for"
              name="text"
              setValue={(value) => {
                setFormData((prev) => ({ ...prev, description: value }));
              }}
            />
          </div>
        </div>
        <div className="flex p-5.5 gap-x-3">
          <div className="flex-1" />
          {actionRouteAddButtons.map((item) => (
            <Button
              key={item.label}
              type={item.type && item.type}
              color={item.variant}
              label={item.label}
              size="md"
              onClick={item.onClick}
              isDisabled={item.isDisabled}
            />
          ))}
        </div>
      </form>

    </CommonModal>
  );
};

RouteAddEditModal.propTypes = {
  onCloseModal: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  data: PropTypes.shape({
    name: PropTypes.string,
    date: PropTypes.string,
    description: PropTypes.string,
  }).isRequired,
  onSubmit: PropTypes.func.isRequired,
  isSaving: PropTypes.bool.isRequired,
};

export default RouteAddEditModal;
